import { useDataPreparationOverlayElements } from "@/data-preparation-tool/data-preparation-overlay-context";
import { ScanColoring } from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-slice";
import { Projection } from "@/data-preparation-tool/ui/projection-switch";
import { RenderOrder } from "@/data-preparation-tool/utils/entity-utils";
import { useChangeCache } from "@/hooks/use-change-cache";
import { PointCloudObject } from "@/object-cache";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  dataComparisonColorsSorted,
  grey,
  useIsDarkScheme,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { AdaptivePointsMaterial } from "@faro-lotv/lotv";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  Color,
  ColorRepresentation,
  Material,
  Plane,
  PointsMaterial,
} from "three";

/** The maximum lightness for light mode, to ensure sufficient contrast. */
const MAX_LIGHTNESS_LIGHT = 0.4;
/** The minimum lightness for dark mode, to ensure sufficient contrast. */
const MIN_LIGHTNESS_DARK = 0.6;

/**
 * @param index The index to get the color for.
 * @param colors the color array used to select color for the given index
 * @returns The comparison color for the given index.
 */
export function colorForIndex<Color extends ColorRepresentation>(
  index: number,
  colors: Color[],
): Color {
  return colors[index % colors.length];
}

interface UsePointCloudMaterialsOptions {
  /** The point clouds to adapt the materials for */
  pointClouds: PointCloudObject[];

  /** The IDs of point clouds to highlight. If defined, all non-highlighted point clouds are rendered grey. */
  highlightedIds?: GUID[];

  /** The used scan coloring mode @default color by scan */
  scanColoring?: ScanColoring;

  /**
   * The explicit colors to use to select color for pointClouds.
   *
   * @default dataComparisonColorsSorted
   */
  colors?: ColorRepresentation[];

  /** The clipping planes to use for the custom materials */
  clippingPlanes?: Plane[];
}

/**
 * Sets materials with distinct colors and convenient transparency to render multiple point clouds.
 */
export function usePointCloudMaterials({
  pointClouds,
  scanColoring = ScanColoring.byScan,
  highlightedIds,
  colors = dataComparisonColorsSorted,
  clippingPlanes,
}: UsePointCloudMaterialsOptions): void {
  const { projection } = useDataPreparationOverlayElements();
  const isDarkMode = useIsDarkScheme();
  const oldMaterialMap = useRef(new Map<GUID, Material>());
  const materialMap = useRef(new Map<GUID, PointsMaterial>());
  const adaptiveMaterialMap = useRef(new Map<GUID, AdaptivePointsMaterial>());

  const allHighlighted = !highlightedIds || highlightedIds.length === 0;

  const isHighlightedMap = useMemo(() => {
    const map: Record<GUID, boolean> = {};

    for (const pointCloud of pointClouds) {
      const { id } = pointCloud.iElement;
      map[id] = allHighlighted || highlightedIds.includes(id);
    }

    return map;
  }, [allHighlighted, pointClouds, highlightedIds]);

  // Create new materials for each point cloud and remember the old ones
  useEffect(() => {
    for (const pointCloud of pointClouds) {
      const { id } = pointCloud.iElement;

      if (!oldMaterialMap.current.has(id)) {
        oldMaterialMap.current.set(id, pointCloud.material);
      }

      const material = getCachedOrDefault(
        materialMap.current,
        id,
        () =>
          new PointsMaterial({
            transparent: true,
            opacity: 0.1,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: false,
            size: 2,
            clipIntersection: false,
          }),
      );
      const adaptiveMaterial = getCachedOrDefault(
        adaptiveMaterialMap.current,
        id,
        () => new AdaptivePointsMaterial({ minSize: 1, maxSize: 2 }),
      );

      // Depth testing: In original color mode for proper color display,
      // in 3D mode to reduce visual clutter
      const depthTesting =
        scanColoring === ScanColoring.originalColor ||
        projection === Projection.threeDimensional;

      material.depthWrite = depthTesting;
      material.depthTest = depthTesting;

      // To avoid cases where the material isn't correctly assigned to the cached material,
      // the material is always re-assigned here
      pointCloud.material =
        scanColoring === ScanColoring.originalColor
          ? adaptiveMaterial
          : material;
    }
  }, [pointClouds, scanColoring, projection]);

  // Assign the correct material and render order to each point cloud
  useEffect(() => {
    for (const pointCloud of pointClouds) {
      const { id } = pointCloud.iElement;

      const isHighlighted = isHighlightedMap[id];

      // Update render order
      pointCloud.renderOrder = isHighlighted
        ? RenderOrder.highlightedScans
        : RenderOrder.scans;

      // Get the material based on the scanColoring mode
      const material =
        scanColoring === ScanColoring.originalColor && isHighlighted
          ? adaptiveMaterialMap.current.get(id)
          : materialMap.current.get(id);

      if (!material) {
        console.warn("Missing material for pointCloud", id);
        continue;
      }

      // Assign the material to the point cloud
      pointCloud.material = material;
    }
  }, [pointClouds, scanColoring, isHighlightedMap]);

  const colorScheme = useMemo(() => {
    if (isDarkMode) {
      // Dark theme
      return {
        highlighted: colors.map((color) =>
          clampLightness(color, MIN_LIGHTNESS_DARK, 1.0),
        ),
        intensity: new Color(grey[200]),
        muted: new Color(grey[700]),
      };
    }

    // Light theme
    return {
      highlighted: colors.map((color) =>
        clampLightness(color, 0.0, MAX_LIGHTNESS_LIGHT),
      ),
      intensity: new Color(grey[900]),
      muted: new Color(grey[400]),
    };
  }, [isDarkMode, colors]);

  // Update scan colors
  useEffect(() => {
    for (const [index, pointCloud] of pointClouds.entries()) {
      const { id } = pointCloud.iElement;
      const isHighlighted = isHighlightedMap[id];
      const material = materialMap.current.get(id);
      let color: Color = isHighlighted
        ? colorForIndex(index, colorScheme.highlighted)
        : colorScheme.muted;

      if (scanColoring === ScanColoring.intensity && allHighlighted) {
        // With intensity coloring, default to gray scale when nothing is selected / hovered
        color = colorScheme.intensity;
      }

      if (!material) {
        console.warn("Missing material for pointCloud", pointCloud.iElement.id);
        continue;
      }

      material.color = color;
      material.needsUpdate = true;
    }
  }, [
    pointClouds,
    isHighlightedMap,
    allHighlighted,
    scanColoring,
    colorScheme,
  ]);

  const hasChanged = useChangeCache();

  // Update clipping planes whenever they or the materials change
  useFrame(() => {
    const clippingPlanesChanged = hasChanged("clippingPlanes", clippingPlanes);

    for (const pointCloud of pointClouds) {
      const { id } = pointCloud.iElement;

      if (
        clippingPlanesChanged ||
        hasChanged(`material@${id}`, pointCloud.material)
      ) {
        pointCloud.material.clippingPlanes = clippingPlanes ?? null;
        pointCloud.material.needsUpdate = true;
      }
    }
  });

  // Reset to old materials and dispose newly created ones when unmounting
  useNonExhaustiveEffect(
    () => () => {
      // Revert to previously used materials
      for (const [id, oldMaterial] of oldMaterialMap.current.entries()) {
        const pointCloud = pointClouds.find((pc) => pc.iElement.id === id);
        if (pointCloud) {
          pointCloud.material = oldMaterial;
        }
      }

      // Dispose newly created materials
      for (const material of materialMap.current.values()) {
        material.dispose();
      }
      for (const material of adaptiveMaterialMap.current.values()) {
        material.dispose();
      }
    },
    [],
  );
}

/**
 *
 * @param color The color to clamp the lightness of.
 * @param min The minimum lightness the color should have, between 0.0 and 1.0.
 * @param max The maximum lightness the color should have, between 0.0 and 1.0.
 * @returns The color with clamped lightness applied.
 */
function clampLightness(
  color: ColorRepresentation,
  min: number = 0,
  max: number = 1,
): Color {
  const { h, s, l } = new Color(color).getHSL({ h: 0, s: 0, l: 0 });
  const clampedL = Math.min(Math.max(l, min), max);
  return new Color().setHSL(h, s, clampedL);
}

function getCachedOrDefault<T>(
  cache: Map<GUID, T>,
  id: GUID,
  createDefault: () => T,
): T {
  const cached = cache.get(id);

  if (cached) {
    return cached;
  }

  const defaultValue = createDefault();
  cache.set(id, defaultValue);
  return defaultValue;
}
